import {
  DEFAULT_QUIZ_STORAGE_NAMESPACE,
  getQuizStorageKeys,
  getStartedFunnel,
  hasCompletedQuiz,
  MINI_QUIZ_STORAGE_NAMESPACE,
  type QuizStorageNamespace,
} from '../defs/localStorage';
import { type Quiz } from '../defs/quiz';

export interface LoginQuizPersistCandidate {
  namespace: QuizStorageNamespace;
  quiz: Quiz;
}

function readJson(key: string): unknown {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return undefined;
  }
  if (raw === null) {
    return undefined;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/**
 * Strict expiry read: the stored `.expiry` must be present and in the future.
 * Deliberately not `isQuizLocalStorageExpired`, which mints a fresh expiry
 * when none is stored — on this path a missing expiry means "not eligible",
 * never "give it two more weeks".
 */
function isUnexpired(namespace: QuizStorageNamespace): boolean {
  const expiry = readJson(getQuizStorageKeys(namespace).expiry);
  if (typeof expiry !== 'string') {
    return false;
  }
  const expiresAt = new Date(expiry);
  return !Number.isNaN(expiresAt.getTime()) && expiresAt > new Date();
}

function readLocalQuiz(namespace: QuizStorageNamespace): Quiz | undefined {
  const quiz = readJson(getQuizStorageKeys(namespace).local);
  if (typeof quiz !== 'object' || quiz === null) {
    return undefined;
  }
  // `quiz.local` is written by `saveQuizToLocalStorage` from a `Quiz`; the
  // same unvalidated read `loadQuizFromLocalStorage` makes at signup.
  return quiz as Quiz;
}

function readCandidate(
  namespace: QuizStorageNamespace
): LoginQuizPersistCandidate | null {
  // Each funnel flags completion at its signup hand-off (`hasCompletedQuiz`);
  // the flag shares the answers' 2-week lifetime rather than the 24h resume
  // windows, so a visitor who finished days ago still qualifies.
  if (!hasCompletedQuiz(namespace) || !isUnexpired(namespace)) {
    return null;
  }

  const quiz = readLocalQuiz(namespace);
  if (quiz === undefined) {
    return null;
  }

  return { namespace, quiz };
}

/**
 * The locally stored quiz a logging-in visitor finished without signing up,
 * if any: present, unexpired, and carrying its funnel's completion marker.
 * Anything short of that — mid-quiz progress, a stale quiz, a quiz with no
 * expiry — is left alone for the normal resume paths.
 *
 * When both funnels qualify, the one the visitor most recently stepped into
 * (`getStartedFunnel`) wins; otherwise the full quiz is preferred.
 */
export function readLoginQuizPersistCandidate(): LoginQuizPersistCandidate | null {
  const startedFunnel = getStartedFunnel();
  const namespaces: QuizStorageNamespace[] =
    startedFunnel === MINI_QUIZ_STORAGE_NAMESPACE
      ? [MINI_QUIZ_STORAGE_NAMESPACE, DEFAULT_QUIZ_STORAGE_NAMESPACE]
      : [DEFAULT_QUIZ_STORAGE_NAMESPACE, MINI_QUIZ_STORAGE_NAMESPACE];

  for (const namespace of namespaces) {
    const candidate = readCandidate(namespace);
    if (candidate !== null) {
      return candidate;
    }
  }

  return null;
}
